// Deterministic models of React behaviour for Lessons 2.6 and 2.7. They simulate what
// React does with state, keys, controlled inputs, effects, stale responses and hash
// routes; they do not run React's reconciler or make requests.

export interface UiTask {
  id: number;
  title: string;
  completed: boolean;
}
export const reactTasks = (): UiTask[] => [
  { id: 1, title: 'Prepare the demo', completed: false },
  { id: 4, title: "Read Maya's notes", completed: true },
  { id: 8, title: 'Check the examples', completed: false },
];
const copyTasks = (rows: UiTask[]) => rows.map((row) => ({ ...row }));

// --- react-render: a setter schedules a render; a mutated array does not ---------------

export interface RenderState {
  /** The array the component's state variable currently refers to. */
  state: UiTask[];
  /** The rows the screen shows: what the last render read. */
  rendered: UiTask[];
  renders: number;
  message: string;
}
export const initialRender = (): RenderState => ({
  state: reactTasks(),
  rendered: reactTasks(),
  renders: 1,
  message: 'The component rendered once from its initial state. Change the state in two ways.',
});
export type RenderAction = 'set-complete' | 'mutate' | 'add' | 'reset';
export function renderTransition(state: RenderState, action: RenderAction): RenderState {
  switch (action) {
    case 'reset':
      return initialRender();
    case 'set-complete': {
      const next = state.state.map((row) => (row.id === 1 ? { ...row, completed: true } : row));
      return {
        state: next,
        rendered: copyTasks(next),
        renders: state.renders + 1,
        message:
          'setTasks received a new array. React compared it with the previous one, saw a different value and rendered again, so the screen matches the state.',
      };
    }
    case 'mutate': {
      const first = state.state[0];
      if (!first) return state;
      if (first.completed)
        return {
          ...state,
          message:
            'tasks[0].completed is already true, so nothing changed. A direct change would still not tell React to render.',
        };
      first.completed = true;
      return {
        ...state,
        message:
          'The array changed in place. React was never told, so the screen still shows the old rows. The state and the screen now disagree.',
      };
    }
    case 'add': {
      const nextId = Math.max(...state.state.map((row) => row.id)) + 1;
      const next = [
        ...state.state,
        { id: nextId, title: 'Write the release note', completed: false },
      ];
      const hidden = state.rendered.some(
        (row) => state.state.find((item) => item.id === row.id)?.completed !== row.completed,
      );
      return {
        state: next,
        rendered: copyTasks(next),
        renders: state.renders + 1,
        message: hidden
          ? 'setTasks received a new array and React rendered again. This render read the current state, so the earlier direct change appears now too.'
          : 'setTasks received a new array with one more task. React rendered again and the new row appeared.',
      };
    }
  }
}

// --- react-keys: a key says which row an instance belongs to -------------------------

export type KeyRule = 'index' | 'id';
export interface KeyedRow {
  id: number;
  title: string;
  draft: string;
}
/**
 * Simulate React reconciling a list of rows that each hold their own draft text.
 * With index keys an instance stays at its position; with id keys it follows its task.
 */
export function keyedRows(
  rule: KeyRule,
  draftTask: number,
  draft: string,
  deletedTask: number | null,
): { rows: KeyedRow[]; outcome: 'kept' | 'moved' | 'lost' | 'removed' | 'none'; message: string } {
  const before = reactTasks();
  const drafts = before.map((task) => (task.id === draftTask ? draft : ''));
  const after = before.filter((task) => task.id !== deletedTask);
  if (deletedTask === null || after.length === before.length)
    return {
      rows: before.map((task, index) => ({
        id: task.id,
        title: task.title,
        draft: drafts[index] ?? '',
      })),
      outcome: 'none',
      message: draft
        ? `The draft belongs to the row for task ${draftTask}. Delete a task and compare the key rules.`
        : 'Type a draft into a row, then delete a task.',
    };
  let rows: KeyedRow[];
  if (rule === 'id') {
    rows = after.map((task) => ({
      id: task.id,
      title: task.title,
      draft: task.id === draftTask ? draft : '',
    }));
  } else {
    // Instance at position i keeps its draft; the instance beyond the new length unmounts.
    rows = after.map((task, index) => ({
      id: task.id,
      title: task.title,
      draft: drafts[index] ?? '',
    }));
  }
  if (!draft)
    return {
      rows,
      outcome: 'none',
      message: `Task ${deletedTask} was removed. No draft was typed, so nothing is at risk yet.`,
    };
  const holder = rows.find((row) => row.draft === draft);
  if (deletedTask === draftTask && rule === 'id')
    return {
      rows,
      outcome: 'removed',
      message: `Task ${draftTask} was deleted, and its row instance left with it. The draft belonged to that task, so removing both is correct.`,
    };
  if (!holder)
    return {
      rows,
      outcome: 'lost',
      message: `With index keys the list is one row shorter, so React unmounted the last instance, which held the draft for task ${draftTask}. The text is gone even though task ${draftTask} is still there.`,
    };
  if (holder.id === draftTask)
    return {
      rows,
      outcome: 'kept',
      message: `With id keys the instance for task ${draftTask} is matched by its key, so the draft stays with task ${draftTask}.`,
    };
  return {
    rows,
    outcome: 'moved',
    message: `With index keys React reused the instance at that position for a different task, so the draft typed for task ${draftTask} now appears under task ${holder.id}.`,
  };
}

// --- react-controlled: state owns the value; the input shows it ------------------------

export const TITLE_RULE =
  'Use a title with 1–80 characters after trimming spaces, tabs and line breaks.';
export function normalizeTitle(
  value: string,
): { ok: true; title: string } | { ok: false; message: string } {
  const title = value.replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '');
  if (/[\uD800-\uDFFF]/u.test(title))
    return {
      ok: false,
      message: 'Use valid Unicode text; an unpaired surrogate is not a character.',
    };
  const count = Array.from(title).length;
  if (count < 1 || count > 80) return { ok: false, message: TITLE_RULE };
  return { ok: true, title };
}
export type SubmitOutcome =
  | { kind: 'busy'; message: string }
  | { kind: 'rejected'; message: string }
  | { kind: 'sent'; request: string; message: string };
export function controlledSubmit(draft: string, busy: boolean): SubmitOutcome {
  if (busy)
    return {
      kind: 'busy',
      message:
        'The page is busy, so the submit button and the input are disabled. The draft stays in state until the current request finishes.',
    };
  const result = normalizeTitle(draft);
  if (!result.ok)
    return {
      kind: 'rejected',
      message: `${result.message} No request was sent, and the draft stays in the box so it can be corrected.`,
    };
  return {
    kind: 'sent',
    request: `POST /tasks\nContent-Type: application/json\n\n${JSON.stringify({ title: result.title })}`,
    message:
      'The handler read the title from state, not from the DOM, sent the request and set the draft back to an empty string. The input shows the new state.',
  };
}

// --- react-lift: who must know that the page is busy? ---------------------------------

/** Where the busy flag lives: inside the form, or in the page that contains every control. */
export type BusyOwner = 'form' | 'page';
export type LiftPhase = 'idle' | 'creating' | 'done';
export interface LiftDisabled {
  input: boolean;
  add: boolean;
  /** Keyed by task id: whether that row's Delete button is disabled. */
  deletes: Record<number, boolean>;
}
export interface LiftState {
  owner: BusyOwner;
  phase: LiftPhase;
  rows: UiTask[];
  /** The title the create request carries while it is in flight. */
  draft: string;
  /** True once a delete ran while the create was still in flight. */
  overlapped: boolean;
  disabled: LiftDisabled;
  message: string;
}
export type LiftAction =
  | { kind: 'start-create' }
  | { kind: 'press-delete'; id: number }
  | { kind: 'finish-create' }
  | { kind: 'reset' };
const LIFT_DRAFT = 'Write the release note';
export function liftDisabled(owner: BusyOwner, phase: LiftPhase, rows: UiTask[]): LiftDisabled {
  const busy = phase === 'creating';
  const rowsDisabled = busy && owner === 'page';
  return {
    input: busy,
    add: busy,
    deletes: Object.fromEntries(rows.map((row) => [row.id, rowsDisabled])),
  };
}
export const initialLift = (owner: BusyOwner = 'form'): LiftState => ({
  owner,
  phase: 'idle',
  rows: reactTasks(),
  draft: LIFT_DRAFT,
  overlapped: false,
  disabled: liftDisabled(owner, 'idle', reactTasks()),
  message:
    owner === 'form'
      ? 'The form keeps its own busy flag. Start a create, then press Delete on task 4 before it finishes.'
      : 'The page keeps one busy flag and passes it to every control. Start a create, then press Delete on task 4 before it finishes.',
});
export function liftTransition(state: LiftState, action: LiftAction): LiftState {
  const withControls = (next: Omit<LiftState, 'disabled'>): LiftState => ({
    ...next,
    disabled: liftDisabled(next.owner, next.phase, next.rows),
  });
  switch (action.kind) {
    case 'reset':
      return initialLift();
    case 'start-create':
      if (state.phase === 'creating')
        return { ...state, message: 'A create is already in flight. Finish it first.' };
      return withControls({
        ...state,
        phase: 'creating',
        draft: LIFT_DRAFT,
        overlapped: false,
        message:
          state.owner === 'form'
            ? 'The form set its own busy flag, so its input and Add button are disabled. The rows cannot see that flag, so every Delete button still works.'
            : 'The page set its busy flag and passed it down as a prop. The input, the Add button and every Delete button are disabled until the result is known.',
      });
    case 'press-delete': {
      const row = state.rows.find((item) => item.id === action.id);
      if (!row) return { ...state, message: `Task ${action.id} is not on the list.` };
      if (state.phase !== 'creating') {
        return withControls({
          ...state,
          rows: state.rows.filter((item) => item.id !== action.id),
          message: `Task ${action.id} was deleted. No other write was running, so the list shows exactly what the server holds.`,
        });
      }
      if (state.owner === 'page')
        return {
          ...state,
          message: `Nothing happened. The Delete button for task ${action.id} reads the page's busy flag through its props and is disabled, so the create finishes alone.`,
        };
      return withControls({
        ...state,
        rows: state.rows.filter((item) => item.id !== action.id),
        overlapped: true,
        message: `The Delete button was enabled, so a second write started while the create was still in flight. Two writes now overlap. The refresh that follows the create may answer before or after task ${action.id} is removed, so the page cannot say which list is current.`,
      });
    }
    case 'finish-create': {
      if (state.phase !== 'creating')
        return { ...state, message: 'No create is running. Start one first.' };
      const created: UiTask = { id: 9, title: LIFT_DRAFT, completed: false };
      if (state.overlapped) {
        // The refresh answered while the delete was still running, so the deleted row is back.
        return withControls({
          ...state,
          phase: 'done',
          draft: '',
          rows: [...reactTasks(), created],
          message:
            'The create finished and its refresh replaced the list. The refresh was answered while the delete was still running, so task 4 is back on screen although the server may remove it a moment later. The page cannot tell Maya which list is current.',
        });
      }
      return withControls({
        ...state,
        phase: 'done',
        draft: '',
        rows: [...state.rows, created],
        message:
          'The create finished. The refresh shows the stored list with the new task 9, the input is cleared and every control is enabled again. Only one write ran, so the page knows exactly what was established.',
      });
    }
  }
}

// --- react-effect: a load runs when the component appears or a dependency changes -------

export type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; rows: UiTask[] }
  | { kind: 'failed'; message: string };
export type EffectFilter = 'all' | 'open';
export interface EffectState {
  mounted: boolean;
  filter: EffectFilter;
  load: LoadState;
  pending: boolean;
  log: string[];
  message: string;
}
export const initialEffect = (): EffectState => ({
  mounted: false,
  filter: 'all',
  load: { kind: 'idle' },
  pending: false,
  log: [],
  message: 'The task page is not on screen. Mount it to run its effect.',
});
export type EffectAction =
  | 'mount'
  | 'respond-rows'
  | 'respond-empty'
  | 'respond-error'
  | 'change-filter'
  | 'unmount'
  | 'reset';
const requestFor = (filter: EffectFilter) =>
  filter === 'all' ? 'GET /tasks' : 'GET /tasks?completed=false';
export function effectTransition(state: EffectState, action: EffectAction): EffectState {
  const log = (...lines: string[]) => [...state.log, ...lines].slice(-8);
  switch (action) {
    case 'reset':
      return initialEffect();
    case 'mount':
      if (state.mounted) return { ...state, message: 'The page is already mounted.' };
      return {
        mounted: true,
        filter: state.filter,
        load: { kind: 'loading' },
        pending: true,
        log: log(`mount → effect runs (filter=${state.filter}) → ${requestFor(state.filter)}`),
        message:
          'The component rendered its loading state first, then its effect sent the request. Nothing is on the list until a response arrives.',
      };
    case 'respond-rows':
    case 'respond-empty':
    case 'respond-error': {
      if (!state.pending)
        return {
          ...state,
          message: state.mounted
            ? 'No request is waiting for a response.'
            : 'The component is not mounted. A late response has no component to update; the cleanup flag makes the effect ignore it.',
        };
      const rows =
        action === 'respond-rows'
          ? reactTasks().filter((row) => state.filter === 'all' || !row.completed)
          : [];
      const load: LoadState =
        action === 'respond-error'
          ? {
              kind: 'failed',
              message: 'The task database is unavailable. Try reading again after it recovers.',
            }
          : { kind: 'ready', rows };
      return {
        ...state,
        load,
        pending: false,
        log: log(
          action === 'respond-error'
            ? 'response: 503 → setLoad(failed)'
            : `response: 200 with ${rows.length} row(s) → setLoad(ready)`,
        ),
        message:
          action === 'respond-error'
            ? 'The effect set a failed state with the message. The page shows the failure instead of an empty list.'
            : rows.length
              ? 'The effect set a ready state with rows and React rendered them.'
              : 'The effect set a ready state with zero rows. Empty is a successful answer, so the page says there are no tasks in this view.',
      };
    }
    case 'change-filter': {
      if (!state.mounted)
        return { ...state, message: 'Mount the page before changing its filter.' };
      const filter: EffectFilter = state.filter === 'all' ? 'open' : 'all';
      return {
        ...state,
        filter,
        load: { kind: 'loading' },
        pending: true,
        log: log(
          `cleanup (filter=${state.filter})${state.pending ? ' → the unanswered request will be ignored' : ''}`,
          `effect runs (filter=${filter}) → ${requestFor(filter)}`,
        ),
        message:
          'The filter is a dependency of the effect. React ran the previous cleanup, then ran the effect again with the new filter and a new request.',
      };
    }
    case 'unmount':
      if (!state.mounted) return { ...state, message: 'The page is already unmounted.' };
      return {
        ...state,
        mounted: false,
        load: { kind: 'idle' },
        pending: false,
        log: log(`unmount → cleanup (filter=${state.filter})`),
        message:
          'Unmounting ran the cleanup. Any response still travelling is ignored, because there is no component left to update.',
      };
  }
}

// --- react-stale: ignore an answer to an old question ----------------------------------

export interface ProjectRow {
  id: number;
  title: string;
  assignee: string | null;
}
export const projectNames: Record<number, string> = { 1: 'Apollo', 2: 'Support' };
export const projectRows = (projectId: number): ProjectRow[] =>
  projectId === 1
    ? [
        { id: 9, title: 'Write the release note', assignee: 'Ravi' },
        { id: 10, title: 'Check the dates', assignee: null },
      ]
    : [{ id: 11, title: 'Draft the welcome email', assignee: 'Ravi' }];
export interface StaleState {
  guard: boolean;
  route: number | null;
  pending: number[];
  rows: ProjectRow[] | null;
  shownFor: number | null;
  log: string[];
  message: string;
}
export const initialStale = (guard = true): StaleState => ({
  guard,
  route: null,
  pending: [],
  rows: null,
  shownFor: null,
  log: [],
  message: 'Open a project page, then open another before the first answer arrives.',
});
export type StaleAction =
  'open-1' | 'open-2' | 'deliver-1' | 'deliver-2' | 'toggle-guard' | 'reset';
export function staleTransition(state: StaleState, action: StaleAction): StaleState {
  const log = (...lines: string[]) => [...state.log, ...lines].slice(-8);
  switch (action) {
    case 'reset':
      return initialStale(state.guard);
    case 'toggle-guard':
      return {
        ...initialStale(!state.guard),
        message: `Cleanup guard ${state.guard ? 'off' : 'on'}. Replay the same clicks.`,
      };
    case 'open-1':
    case 'open-2': {
      const id = action === 'open-1' ? 1 : 2;
      if (state.route === id) return { ...state, message: `Project ${id} is already open.` };
      const lines = [`route → #/projects/${id}: effect runs → GET /projects/${id}/tasks`];
      if (state.route !== null && state.pending.includes(state.route))
        lines.unshift(
          state.guard
            ? `cleanup for project ${state.route}: its answer will be ignored`
            : `no cleanup: the answer for project ${state.route} can still arrive`,
        );
      return {
        ...state,
        route: id,
        pending: state.pending.includes(id) ? state.pending : [...state.pending, id],
        rows: null,
        shownFor: null,
        log: log(...lines),
        message: `Page for ${projectNames[id]} is loading.`,
      };
    }
    case 'deliver-1':
    case 'deliver-2': {
      const id = action === 'deliver-1' ? 1 : 2;
      if (!state.pending.includes(id))
        return { ...state, message: `No request for project ${id} is outstanding.` };
      const pending = state.pending.filter((item) => item !== id);
      if (state.route === id)
        return {
          ...state,
          pending,
          rows: projectRows(id),
          shownFor: id,
          log: log(`response for ${id} → setLoad(ready): ${projectNames[id]} tasks shown`),
          message: `The answer for ${projectNames[id]} arrived while its page is open. The rows match the heading.`,
        };
      if (state.guard)
        return {
          ...state,
          pending,
          log: log(`response for ${id} → ignored: the effect that asked was cleaned up`),
          message: `The late answer for ${projectNames[id]} was ignored. The ${state.route === null ? 'page' : projectNames[state.route]} page keeps waiting for its own answer.`,
        };
      return {
        ...state,
        pending,
        rows: projectRows(id),
        shownFor: id,
        log: log(
          `response for ${id} → setLoad(ready) under the ${state.route === null ? 'current' : projectNames[state.route]} heading`,
        ),
        message: `Without the guard, the late answer for ${projectNames[id]} replaced the rows. The heading says ${state.route === null ? 'another page' : projectNames[state.route]} but the tasks belong to ${projectNames[id]}.`,
      };
    }
  }
}
export const staleMismatch = (state: StaleState) =>
  state.shownFor !== null && state.route !== null && state.shownFor !== state.route;

// --- react-router: the hash changes the page without asking the server -----------------

export const MAX_ID = 9007199254740991;
export type Route =
  | { page: 'tasks' }
  | { page: 'projects' }
  | { page: 'project'; id: number }
  | { page: 'missing'; hash: string };
export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, '');
  if (path === '' || path === '/') return { page: 'tasks' };
  if (path === '/projects') return { page: 'projects' };
  const match = /^\/projects\/([1-9][0-9]*)$/.exec(path);
  if (match) {
    const id = Number(match[1]);
    if (Number.isSafeInteger(id) && id <= MAX_ID) return { page: 'project', id };
  }
  return { page: 'missing', hash };
}
export const routeComponent = (route: Route) =>
  route.page === 'tasks'
    ? 'TaskPage'
    : route.page === 'projects'
      ? 'ProjectsPage'
      : route.page === 'project'
        ? `ProjectPage (id ${route.id})`
        : 'NotFound';
export type RouteMode = 'hash' | 'path';
export interface RouterState {
  mode: RouteMode;
  history: string[];
  index: number;
  requests: string[];
  message: string;
}
export const initialRouter = (mode: RouteMode = 'hash'): RouterState => ({
  mode,
  history: [mode === 'hash' ? '#/' : '/'],
  index: 0,
  requests: ['GET / (first page load)'],
  message: 'The page loaded once. Follow links, go back and reload, and watch the server log.',
});
export const currentLocation = (state: RouterState) => state.history[state.index] ?? '#/';
/** The hash form of the current location, so one parser serves both modes. */
export const currentHash = (state: RouterState) => {
  const location = currentLocation(state);
  return state.mode === 'hash' ? location : `#${location === '/' ? '/' : location}`;
};
export type RouterAction =
  | { kind: 'go'; target: '/' | '/projects' | '/projects/1' }
  | { kind: 'back' }
  | { kind: 'reload' }
  | { kind: 'toggle-mode' }
  | { kind: 'reset' };
export function routerTransition(state: RouterState, action: RouterAction): RouterState {
  switch (action.kind) {
    case 'reset':
      return initialRouter(state.mode);
    case 'toggle-mode':
      return {
        ...initialRouter(state.mode === 'hash' ? 'path' : 'hash'),
        message:
          state.mode === 'hash'
            ? 'Path routes: links use pushState, so clicking still asks the server for nothing. Reload asks the server for the full path.'
            : 'Hash routes: the part after # never leaves the browser.',
      };
    case 'go': {
      const location = state.mode === 'hash' ? `#${action.target}` : action.target;
      if (currentLocation(state) === location)
        return { ...state, message: 'That page is already open.' };
      const history = [...state.history.slice(0, state.index + 1), location];
      return {
        ...state,
        history,
        index: history.length - 1,
        message:
          state.mode === 'hash'
            ? `location.hash became ${location}. The hashchange event fired, the route was parsed and the matching component rendered. No request left the browser.`
            : `history.pushState changed the path to ${location} without a request. The server has not seen this URL yet.`,
      };
    }
    case 'back': {
      if (state.index === 0)
        return { ...state, message: 'There is no earlier entry in this history.' };
      return {
        ...state,
        index: state.index - 1,
        message:
          state.mode === 'hash'
            ? 'The browser moved back one entry and fired hashchange. The page re-rendered from the earlier hash, still without a request.'
            : 'The browser moved back one entry and fired popstate. The page re-rendered from the earlier path.',
      };
    }
    case 'reload': {
      const location = currentLocation(state);
      const request = state.mode === 'hash' ? 'GET /' : `GET ${location}`;
      const served =
        state.mode === 'hash'
          ? 'The server returned dist/index.html; the browser kept the hash and the page rendered the same route.'
          : location === '/'
            ? 'The server returned dist/index.html.'
            : `The server answered ${location} as an API path or a 404, not as the page. A path router needs the server to return the page for every client route.`;
      return {
        ...state,
        requests: [...state.requests, request].slice(-6),
        message: `Reload asked the server for ${request.replace('GET ', '')}. ${served}`,
      };
    }
  }
}
